import { ChangeEvent, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { getPageURL, PageURL } from 'router/pageURL';
import { Button } from 'components/common';
import { ApplicationsTable } from './ApplicationsTable';
import { TextField } from 'components/common/TextField';
import { removeRoutesFromApplicationRoutes } from 'utils/localStorage';
import { ApplicationFormPageEnum } from '../ApplicationFormPage/types';
import { Alert } from '@mui/material';
import { colors } from '../../variables';
import { useBrokerOnboarding } from 'queries';
import { NewSignupPageSectionEnum } from '../SignupPage/enums';
import { useIsOoCaseCreateFlowEnabled } from 'providers/PosthogProvider';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';

export const ApplicationsListingPage = () => {
  const {
    data: brokerOnboarding,
    isFetched: isBrokerOnboardingFetched,
    isError: isBrokerOnboardingError,
  } = useBrokerOnboarding();
  const navigate = useNavigate();
  const isOoCaseCreateFlowEnabled = useIsOoCaseCreateFlowEnabled();
  const [searchInputValue, setSearchInputValue] = useState('');
  const legacyNewApplicationUrl = `${getPageURL(PageURL.APPLICATION_EDIT, { id: uuidv4() })}?section=${
    ApplicationFormPageEnum.CRITERIA
  }`;
  const newOoApplicationUrl = `${getPageURL(PageURL.APPLICATION_CREATE)}?section=${CaseSidebarItemId.FINANCING}`;
  const newApplicationUrl = isOoCaseCreateFlowEnabled ? newOoApplicationUrl : legacyNewApplicationUrl;

  const isFullSignupCompleted =
    !isBrokerOnboardingError &&
    isBrokerOnboardingFetched &&
    brokerOnboarding?.section === NewSignupPageSectionEnum.COMPLETED;

  const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };

  const newApplicationCreateHandler = () => {
    removeRoutesFromApplicationRoutes();
    navigate(newApplicationUrl);
  };

  return (
    <ApplicationsListingPageBody
      isFullSignupCompleted={isFullSignupCompleted}
      searchInputValue={searchInputValue}
      searchHandler={searchHandler}
      newApplicationCreateHandler={newApplicationCreateHandler}
    />
  );
};

type ApplicationsListingPageBodyProps = {
  isFullSignupCompleted: boolean;
  searchInputValue: string;
  searchHandler: (event: ChangeEvent<HTMLInputElement>) => void;
  newApplicationCreateHandler: () => void;
};

const ApplicationsListingPageBody = ({
  isFullSignupCompleted,
  searchHandler,
  searchInputValue,
  newApplicationCreateHandler,
}: ApplicationsListingPageBodyProps) => {
  return (
    <div className={'w-full px-4 pt-4'}>
      {!isFullSignupCompleted && (
        <Alert severity={'warning'} className={'mb-4'}>
          Your profile is incomplete. Please finish your registration by clicking{' '}
          <Link to={PageURL.SIGNUP} style={{ color: colors.primary }} className={'text-sm no-underline'}>
            here
          </Link>
        </Alert>
      )}
      <div className={'flex items-center justify-between'}>
        <TextField
          onChange={searchHandler}
          placeholder={'Search'}
          className={'rounded-md border border-blue-500'}
          value={searchInputValue}
        />
        <Button variant={'contained'} onClick={newApplicationCreateHandler}>
          <FormattedMessage id={'cta.createNewApplication'} />
        </Button>
      </div>
      <div className={'mt-8 flex flex-col items-center'}>
        <ApplicationsTable searchInputValue={searchInputValue} />
      </div>
    </div>
  );
};
